import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { Category, Team, TeamsService } from '../../../services/teams/teams.service';
import {
  PlaidSandboxAccount,
  PlaidSandboxCustomTransactionInput,
  PlaidSandboxCurrentAccountData,
  PlaidSandboxScenarioResult,
  PlaidSandboxService,
  PlaidSandboxTemplate,
  PlaidSandboxWipeResult,
} from '../../../services/plaid-sandbox/plaid-sandbox.service';

type PlaidSandboxDialogData = {
  user: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
};

type PlaidSandboxPreviewRow = {
  sourceLabel: string;
  merchant: string;
  teamCategoryName: string;
  seedCategoryName?: string;
  amount: number;
  daysAgo: number;
};

type CustomTransactionFormValue = PlaidSandboxCustomTransactionInput & {
  useSeedCategoryOverride?: boolean;
};

@Component({
  selector: 'app-plaid-sandbox-tool-dialog',
  templateUrl: './plaid-sandbox-tool-dialog.component.html',
  styleUrls: ['./plaid-sandbox-tool-dialog.component.scss']
})
export class PlaidSandboxToolDialogComponent implements OnInit {
  protected readonly seedCategoryOverrideHelpText = 'When enabled, this transaction is stored with a different underlying category than the reimbursement category you are testing.';
  protected readonly form = this.formBuilder.group({
    targetAccountId: ['', Validators.required],
    teamId: ['', Validators.required],
    templateIds: [[]],
    customTransactions: this.formBuilder.array([]),
    wipeBeforeApply: [true]
  }, {
    validators: [this.requireScenarioSelection]
  });

  protected loading = true;
  protected currentDataLoading = false;
  protected applyInFlight = false;
  protected wipeInFlight = false;
  protected loadError = '';
  protected actionError = '';
  protected accounts: PlaidSandboxAccount[] = [];
  protected teams: Team[] = [];
  protected categories: Category[] = [];
  protected templates: PlaidSandboxTemplate[] = [];
  protected currentAccountData: PlaidSandboxCurrentAccountData | null = null;
  protected latestResult: PlaidSandboxScenarioResult | null = null;
  protected latestWipeResult: PlaidSandboxWipeResult | null = null;
  protected templateSearchControl = new UntypedFormControl('');

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly plaidSandboxService: PlaidSandboxService,
    private readonly teamsService: TeamsService,
    private readonly dialogRef: MatDialogRef<PlaidSandboxToolDialogComponent>,
    @Inject(MAT_DIALOG_DATA) protected readonly data: PlaidSandboxDialogData
  ) {}

  ngOnInit(): void {
    this.loadContext();
    this.templateSearchControl.valueChanges.subscribe(() => {
      this.form.get('templateIds')?.markAsTouched();
    });
    this.form.get('targetAccountId')?.valueChanges.subscribe((accountId) => {
      if (accountId) {
        this.latestResult = null;
        this.latestWipeResult = null;
        this.loadCurrentAccountData(String(accountId));
      } else {
        this.currentAccountData = null;
      }
    });
  }

  protected get customTransactionsArray(): UntypedFormArray {
    return this.form.get('customTransactions') as UntypedFormArray;
  }

  protected get customTransactionControls(): AbstractControl[] {
    return this.customTransactionsArray.controls;
  }

  protected get customTransactionPreview(): PlaidSandboxCustomTransactionInput[] {
    return this.customTransactionsArray.getRawValue().map((transaction: CustomTransactionFormValue) => {
      const normalizedTransaction: PlaidSandboxCustomTransactionInput = {
        merchant: transaction.merchant,
        teamCategoryName: transaction.teamCategoryName,
        amount: Number(transaction.amount),
        daysAgo: Number(transaction.daysAgo)
      };

      if (transaction.useSeedCategoryOverride && transaction.seedCategoryName) {
        normalizedTransaction.seedCategoryName = transaction.seedCategoryName;
      }

      return normalizedTransaction;
    });
  }

  protected get selectedTemplates(): PlaidSandboxTemplate[] {
    const selectedIds = this.form.get('templateIds')?.value || [];
    return this.templates.filter((template) => selectedIds.includes(template.id));
  }

  protected get previewRows(): PlaidSandboxPreviewRow[] {
    const bundleRows = this.selectedTemplates.reduce((rows: PlaidSandboxPreviewRow[], template: PlaidSandboxTemplate) => {
      template.transactions.forEach((transaction) => {
        rows.push({
          sourceLabel: template.label,
          merchant: transaction.merchant,
          teamCategoryName: transaction.teamCategoryName,
          seedCategoryName: transaction.seedCategoryName,
          amount: transaction.amount,
          daysAgo: transaction.daysAgo
        });
      });
      return rows;
    }, []);

    const customRows = this.customTransactionPreview.map((transaction, index) => ({
      sourceLabel: `Custom ${index + 1}`,
      merchant: transaction.merchant,
      teamCategoryName: transaction.teamCategoryName,
      seedCategoryName: transaction.seedCategoryName,
      amount: transaction.amount,
      daysAgo: transaction.daysAgo
    }));

    return [...bundleRows, ...customRows];
  }

  protected get dialogTitle(): string {
    const user = this.data.user;
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
    return name ? `Plaid Sandbox Tools for ${name}` : `Plaid Sandbox Tools for ${user.id}`;
  }

  protected get filteredTemplates(): PlaidSandboxTemplate[] {
    const rawSearchValue = this.templateSearchControl.value;
    const searchValue = typeof rawSearchValue === 'string' ? rawSearchValue.trim().toLowerCase() : '';
    if (!searchValue) {
      return this.templates;
    }
    return this.templates.filter((template) => {
      return template.label.toLowerCase().includes(searchValue)
        || template.purpose.toLowerCase().includes(searchValue)
        || template.tags.some((tag) => tag.toLowerCase().includes(searchValue));
    });
  }

  protected addCustomTransaction(): void {
    this.customTransactionsArray.push(this.createCustomTransactionGroup());
    this.form.updateValueAndValidity();
  }

  protected removeCustomTransaction(index: number): void {
    this.customTransactionsArray.removeAt(index);
    this.form.updateValueAndValidity();
  }

  protected async applyScenario(): Promise<void> {
    if (this.form.invalid || this.applyInFlight) {
      this.form.markAllAsTouched();
      return;
    }

    this.applyInFlight = true;
    this.actionError = '';
    this.latestWipeResult = null;

    try {
      this.latestResult = await this.plaidSandboxService.applyScenario({
        userId: this.data.user.id,
        teamId: String(this.form.get('teamId')?.value || ''),
        targetAccountId: String(this.form.get('targetAccountId')?.value || ''),
        templateIds: [...(this.form.get('templateIds')?.value || [])],
        customTransactions: this.customTransactionPreview,
        wipeBeforeApply: Boolean(this.form.get('wipeBeforeApply')?.value),
        verificationMode: 'standard'
      });
      await this.loadCurrentAccountData(String(this.form.get('targetAccountId')?.value || ''));
    } catch (error: any) {
      this.actionError = error?.data?.error || 'Unable to apply sandbox scenario.';
    } finally {
      this.applyInFlight = false;
    }
  }

  protected async refreshScenarioRun(): Promise<void> {
    if (!this.latestResult?.scenarioRunId) {
      return;
    }
    this.applyInFlight = true;
    this.actionError = '';
    try {
      this.latestResult = await this.plaidSandboxService.getScenarioRun(this.latestResult.scenarioRunId);
      await this.loadCurrentAccountData(String(this.form.get('targetAccountId')?.value || ''));
    } catch (error: any) {
      this.actionError = error?.data?.error || 'Unable to refresh scenario results.';
    } finally {
      this.applyInFlight = false;
    }
  }

  protected async refreshCurrentAccountData(): Promise<void> {
    await this.loadCurrentAccountData(String(this.form.get('targetAccountId')?.value || ''));
  }

  protected async wipeScenario(mode: 'selected_run' | 'last_run' | 'all_tool_generated_for_account'): Promise<void> {
    const targetAccountId = String(this.form.get('targetAccountId')?.value || '');
    if (!targetAccountId || this.wipeInFlight) {
      this.form.get('targetAccountId')?.markAsTouched();
      return;
    }

    if (mode === 'selected_run' && !this.latestResult?.scenarioRunId) {
      this.actionError = 'Apply a scenario before wiping a selected run.';
      return;
    }

    this.wipeInFlight = true;
    this.actionError = '';
    try {
      this.latestWipeResult = await this.plaidSandboxService.wipeScenario({
        userId: this.data.user.id,
        targetAccountId,
        mode,
        scenarioRunId: mode === 'selected_run' ? this.latestResult?.scenarioRunId : undefined
      });
      if (
        mode === 'all_tool_generated_for_account'
        || this.latestWipeResult.scenarioRunId === this.latestResult?.scenarioRunId
      ) {
        this.latestResult = null;
      }
      await this.loadCurrentAccountData(targetAccountId);
    } catch (error: any) {
      this.actionError = error?.data?.error || 'Unable to wipe sandbox data.';
    } finally {
      this.wipeInFlight = false;
    }
  }

  protected close(): void {
    this.dialogRef.close(this.latestResult);
  }

  protected compareById(left: { id?: string } | null, right: { id?: string } | null): boolean {
    return left?.id === right?.id;
  }

  private async loadContext(): Promise<void> {
    this.loading = true;
    this.loadError = '';
    try {
      const [accounts, teams, templates, categories] = await Promise.all([
        this.plaidSandboxService.listUserAccounts(this.data.user.id),
        this.teamsService.getUserMemberTeams(this.data.user.id),
        this.plaidSandboxService.listTemplates(),
        this.teamsService.getCategories()
      ]);

      this.accounts = accounts;
      this.teams = teams || [];
      this.templates = templates;
      this.categories = (categories || []).slice().sort((left, right) => left.name.localeCompare(right.name));

      if (this.accounts.length) {
        this.form.patchValue({targetAccountId: this.accounts[0].accountId});
      }
      if (this.teams.length) {
        this.form.patchValue({teamId: this.teams[0].id});
      }
    } catch (error: any) {
      this.loadError = error?.data?.error || 'Unable to load Plaid sandbox context for this user.';
    } finally {
      this.loading = false;
    }
  }

  protected async loadCurrentAccountData(accountId: string): Promise<void> {
    if (!accountId) {
      this.currentAccountData = null;
      return;
    }
    this.currentDataLoading = true;
    try {
      this.currentAccountData = await this.plaidSandboxService.getCurrentAccountData(this.data.user.id, accountId);
    } catch (error: any) {
      this.actionError = error?.data?.error || 'Unable to load current tool-generated account data.';
    } finally {
      this.currentDataLoading = false;
    }
  }

  private createCustomTransactionGroup() {
    const group = this.formBuilder.group({
      merchant: ['', [Validators.required, Validators.maxLength(45)]],
      teamCategoryName: ['', Validators.required],
      useSeedCategoryOverride: [false],
      seedCategoryName: [''],
      amount: [25, [Validators.required, Validators.min(0.01)]],
      daysAgo: [1, [Validators.required, Validators.min(0)]]
    });

    group.get('useSeedCategoryOverride')?.valueChanges.subscribe((enabled) => {
      const seedCategoryControl = group.get('seedCategoryName');
      if (!enabled) {
        seedCategoryControl?.setValue('', {emitEvent: false});
        seedCategoryControl?.clearValidators();
      } else {
        seedCategoryControl?.setValidators([Validators.required]);
      }
      seedCategoryControl?.updateValueAndValidity({emitEvent: false});
    });

    return group;
  }

  private requireScenarioSelection(control: AbstractControl): ValidationErrors | null {
    const templateIds = control.get('templateIds')?.value || [];
    const customTransactions = control.get('customTransactions')?.value || [];
    return templateIds.length > 0 || customTransactions.length > 0
      ? null
      : {scenarioSelectionRequired: true};
  }
}
